import { useEffect, useRef, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";

type RevealProps = {
  children: ReactNode;
  className?: string;
  /** Extra delay after the element enters view (ms). */
  delay?: number;
};

/**
 * Luxury fade-up on scroll. Replays each page visit and each time the block
 * leaves and re-enters the viewport.
 */
export function Reveal({ children, className = "", delay = 0 }: RevealProps) {
  const { pathname } = useLocation();
  const ref = useRef<HTMLDivElement>(null);
  const seenRef = useRef(false);
  /** False until mounted — keeps no-JS / first paint content visible. */
  const [active, setActive] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    seenRef.current = false;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setActive(true);
      setVisible(true);
      return;
    }

    setActive(true);
    setVisible(false);

    const show = () => {
      if (seenRef.current) return;
      seenRef.current = true;
      setVisible(false);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setVisible(true));
      });
    };

    const hide = () => {
      if (!seenRef.current) return;
      seenRef.current = false;
      setVisible(false);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        if (entry.isIntersecting) show();
        else hide();
      },
      { root: null, rootMargin: "0px 0px -12% 0px", threshold: 0.08 },
    );

    observer.observe(el);

    // Safety: never leave content permanently hidden if IO never fires.
    const safety = window.setTimeout(() => {
      if (!seenRef.current) show();
    }, 2500);

    return () => {
      observer.disconnect();
      window.clearTimeout(safety);
    };
  }, [pathname]);

  const motionClass = !active
    ? ""
    : visible
      ? "lux-reveal-visible"
      : "lux-reveal-pending";

  return (
    <div
      ref={ref}
      className={`${motionClass}${className ? ` ${className}` : ""}`.trim()}
      style={delay ? { animationDelay: `${delay}ms` } : undefined}
    >
      {children}
    </div>
  );
}
